// src/pages/ContactUs.js

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, Phone, MapPin, Send, MessageSquare, HelpCircle, Globe, AlertCircle, CheckCircle } from 'lucide-react';
import { submitContactFormApi } from '../services/api';
import MetaTags from '../components/MetaTags';
import { getContactSchema } from '../utils/schemaGenerator';
import '../components/css/ContactUs.css';

// Regex to validate a 10-digit mobile number
const phoneRegExp = /^\d{10}$/;

// Updated Validation Schema
const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  // --- NEW VALIDATION RULE ---
  mobile: yup.string()
    .required('Mobile number is required')
    .matches(phoneRegExp, 'Mobile number must be exactly 10 digits'),
  // --- END NEW RULE ---
  message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters long'),
});

const ContactUs = () => {
  const [formStatus, setFormStatus] = useState({ message: '', type: '' });

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setFormStatus({ message: '', type: '' });
    try {
      await submitContactFormApi(data);
      setFormStatus({ message: 'Message sent successfully! We will get back to you soon.', type: 'success' });
      reset();
    } catch (error) {
      setFormStatus({ message: 'An error occurred while sending your message. Please try again.', type: 'error' });
      console.error('Contact form submission error:', error);
    }
  };

  // Helper function to allow only numeric input
  const handleNumericInput = (e) => {
    if (!/^\d*$/.test(e.target.value)) {
      e.target.value = e.target.value.replace(/[^\d]/g, '');
    }
  };

  const contactSchema = getContactSchema();

  return (
    <div className="contact-page">
      <MetaTags
        title="Contact BookMyAudition | Support & Enquiries"
        description="Contact BookMyAudition for information, platform support, casting assistance, and general enquiries."
        keywords="contact BookMyAudition, BookMyAudition support, acting auditions contact, casting agency support India"
        canonical="https://web.bookmyaudition.com/contact"
        schema={contactSchema}
      />
      <section className="contact-section">
        <div className="contact-container">
          {/* ... Header and Info Column remain unchanged ... */}
          <div className="contact-header">
            <span className="contact-badge">CONTACT US</span>
            <h1 className="contact-title">GET IN <span>TOUCH</span></h1>
            <p className="contact-subtitle">
              Have questions or need support? Our team is here to help you shine on stage.
            </p>
          </div>

          <div className="contact-main-grid">
            {/* --- INFO COLUMN --- */}
            <div className="contact-info-column">
              <div className="contact-info-card">
                <div className="contact-info-icon"><Mail size={24} /></div>
                <div className="contact-info-content">
                  <h4>Email Us</h4>
                  <p>Our team will respond within 24 hours.</p>
                  <a href="mailto:support@bookmyaudition.com">support@bookmyaudition.com</a>
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-info-icon"><Phone size={24} /></div>
                <div className="contact-info-content">
                  <h4>Call Us</h4>
                  <p>Mon-Fri from 9am to 6pm PST.</p>
                  <a href="tel:+15551234567">+1 (555) 123-4567</a>
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-info-icon"><MapPin size={24} /></div>
                <div className="contact-info-content">
                  <h4>Visit Us</h4>
                  <p>Our headquarters in Hollywood.</p>
                  <strong>123 Casting Lane, Hollywood, CA 90210</strong>
                </div>
              </div>
            </div>

            {/* --- FORM COLUMN (UPDATED) --- */}
            <div className="contact-form-card">
              <h3>Send us a Message</h3>
              <form onSubmit={handleSubmit(onSubmit)} className="contact-form" noValidate>
                <div className="contact-form-grid">
                  <div className="contact-field">
                    <label>First Name</label>
                    <input type="text" {...register('firstName')} placeholder="John" />
                    {errors.firstName && <p className="contact-error">{errors.firstName.message}</p>}
                  </div>

                  <div className="contact-field">
                    <label>Last Name</label>
                    <input type="text" {...register('lastName')} placeholder="Doe" />
                    {errors.lastName && <p className="contact-error">{errors.lastName.message}</p>}
                  </div>

                  <div className="contact-field">
                    <label>Email Address</label>
                    <input type="email" {...register('email')} placeholder="name@example.com" />
                    {errors.email && <p className="contact-error">{errors.email.message}</p>}
                  </div>
                  
                  {/* --- NEW MOBILE FIELD --- */}
                  <div className="contact-field">
                    <label>Mobile Number</label>
                    <input
                      type="tel"
                      {...register('mobile')}
                      placeholder="9876543210"
                      maxLength="10"
                      onInput={handleNumericInput} // Restricts input to numbers
                    />
                    {errors.mobile && <p className="contact-error">{errors.mobile.message}</p>}
                  </div>
                  {/* --- END NEW MOBILE FIELD --- */}

                  <div className="contact-field contact-field-full">
                    <label>Message</label>
                    <textarea {...register('message')} placeholder="How can we help you?" />
                    {errors.message && <p className="contact-error">{errors.message.message}</p>}
                  </div>

                  <div className="contact-field contact-field-full">
                    <button type="submit" className={`contact-submit-btn ${isSubmitting ? 'loading' : ''}`} disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </form>
               {formStatus.message && (
                <div className={`contact-status-message ${formStatus.type}`}>
                  {formStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                  {formStatus.message}
                </div>
              )}
            </div>
          </div>

          {/* --- SUPPORT GRID --- */}
          <div className="contact-support-grid">
            <div className="support-item">
              <div className="support-icon"><MessageSquare size={28} /></div>
              <h4>Live Chat</h4><p>Available 24/7 for Pro members.</p>
            </div>
            <div className="support-item">
              <div className="support-icon"><HelpCircle size={28} /></div>
              <h4>Help Center</h4><p>Browse our extensive documentation.</p>
            </div>
            <div className="support-item">
              <div className="support-icon"><Globe size={28} /></div>
              <h4>Global Support</h4><p>Support in 10+ languages.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;